import { useState, useMemo } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Dashboard,
  Inventory2,
  Build,
  Assignment,
  Schedule,
  LocalShipping,
  Business,
  Assessment,
  Settings,
  ExpandLess,
  ExpandMore,
  QrCode2,
  Notifications,
  Science,
  Biotech,
  LocalHospital,
  Speed,
  Description,
  EventNote,
  SwapHoriz,
  RequestQuote,
  BuildCircle,
  DirectionsCar,
  DevicesOther,
  Campaign,
  NotificationsActive,
  ReportProblem,
  Bloodtype,
  MedicalServices,
  History,
} from '@mui/icons-material'
import { useAuth } from '@context/AuthContext'

const DRAWER_WIDTH = 260

const menuItems = [
  {
    title: 'Dashboard',
    path: '/dashboard',
    icon: <Dashboard />,
  },
  {
    title: 'Assets',
    icon: <DevicesOther />,
    permissions: ['assets.view', 'transfers.view', 'requisitions.view', 'repairs.view', 'vehicles.view', 'fault_reports.view'],
    children: [
      { title: 'All Assets', path: '/assets', icon: <QrCode2 />, permission: 'assets.view' },
      { title: 'Fault Reports', path: '/assets/fault-reports', icon: <ReportProblem />, permission: 'fault_reports.view' },
      { title: 'Transfers', path: '/assets/transfers', icon: <SwapHoriz />, permission: 'transfers.view' },
      { title: 'Requisitions', path: '/assets/requisitions', icon: <RequestQuote />, permission: 'requisitions.view' },
      { title: 'External Repairs', path: '/assets/external-repairs', icon: <BuildCircle />, permission: 'repairs.view' },
      { title: 'Vehicles', path: '/assets/vehicles', icon: <DirectionsCar />, permission: 'vehicles.view' },
    ],
  },
  {
    title: 'Maintenance',
    icon: <Build />,
    permissions: ['work_orders.view', 'pm.view', 'calibrations.view'],
    children: [
      { title: 'Work Orders', path: '/work-orders', icon: <Assignment />, permission: 'work_orders.view' },
      { title: 'PM Schedule', path: '/pm-schedule', icon: <Schedule />, permission: 'pm.view' },
      { title: 'Calibrations', path: '/calibrations', icon: <Speed />, permission: 'calibrations.view' },
    ],
  },
  {
    title: 'Inventory',
    path: '/inventory',
    icon: <Inventory2 />,
    permission: 'inventory.view',
  },
  {
    title: 'Vendors',
    icon: <LocalShipping />,
    permissions: ['vendors.view', 'vendors.contracts'],
    children: [
      { title: 'Vendor List', path: '/vendors', icon: <LocalShipping />, permission: 'vendors.view' },
      { title: 'Service Contracts', path: '/contracts', icon: <Description />, permission: 'vendors.contracts' },
    ],
  },
  {
    title: 'Radiation Oncology',
    icon: <LocalHospital />,
    permissions: ['radiation.view', 'radiation.sources'],
    children: [
      { title: 'Equipment Logs', path: '/radiation-oncology/equipment-logs', icon: <EventNote />, permission: 'radiation.view' },
      { title: 'QA Records', path: '/radiation-oncology/qa-records', icon: <Science />, permission: 'radiation.view' },
      { title: 'Radiation Sources', path: '/radiation-oncology/sources', icon: <Biotech />, permission: 'radiation.sources' },
    ],
  },
  {
    title: 'Clinical Results',
    icon: <MedicalServices />,
    permissions: ['lab_results.view', 'imaging_results.view'],
    children: [
      { title: 'Lab Results', path: '/clinical/lab-results', icon: <Bloodtype />, permission: 'lab_results.view' },
      { title: 'Imaging Results', path: '/clinical/imaging-results', icon: <Science />, permission: 'imaging_results.view' },
    ],
  },
  {
    title: 'Facilities',
    path: '/facilities',
    icon: <Business />,
    permission: 'facilities.view',
  },
  {
    title: 'Reports',
    path: '/reports',
    icon: <Assessment />,
    permission: 'reports.view',
  },
  {
    title: 'Notifications',
    icon: <Notifications />,
    children: [
      { title: 'My Notifications', path: '/notifications', icon: <NotificationsActive /> },
      { title: 'Broadcasts', path: '/notifications/broadcasts', icon: <Campaign />, permission: 'notifications.manage' },
    ],
  },
  {
    title: 'Settings',
    icon: <Settings />,
    children: [
      { title: 'General Settings', path: '/settings', icon: <Settings /> },
      { title: 'Audit Logs', path: '/admin/audit-logs', icon: <History />, permission: 'audit_logs.view' },
    ],
  },
]

const Sidebar = ({ open, onClose, onToggle }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()
  const [expandedItems, setExpandedItems] = useState(['Maintenance'])
  const { hasPermission, hasAnyPermission } = useAuth()

  // Filter menu items based on user permissions
  const filteredMenuItems = useMemo(() => {
    const filterItem = (item) => {
      if (!item.permission && !item.permissions) {
        return item
      }

      if (item.permission && !hasPermission(item.permission)) {
        return null
      }

      if (item.permissions && !hasAnyPermission(item.permissions)) {
        return null
      }

      if (item.children) {
        const filteredChildren = item.children
          .map(filterItem)
          .filter(Boolean)

        if (filteredChildren.length === 0) {
          return null
        }

        return { ...item, children: filteredChildren }
      }

      return item
    }

    return menuItems.map(filterItem).filter(Boolean)
  }, [hasPermission, hasAnyPermission])

  const handleToggle = (title) => {
    setExpandedItems((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    )
  }

  const isActive = (path) => location.pathname === path

  const isParentActive = (children) =>
    children?.some((child) => location.pathname === child.path)

  const handleItemClick = () => {
    if (isMobile) {
      onClose()
    }
  }

  const renderMenuItem = (item, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedItems.includes(item.title)
    const active = isActive(item.path) || isParentActive(item.children)

    if (hasChildren) {
      return (
        <Box key={item.title}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleToggle(item.title)}
              sx={{
                mx: 1,
                borderRadius: 2,
                mb: 0.5,
                backgroundColor: active ? 'rgba(27, 94, 122, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(27, 94, 122, 0.04)',
                },
              }}
            >
              <ListItemIcon sx={{ color: active ? '#1B5E7A' : '#637381', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: active ? 600 : 400,
                  color: active ? '#1B5E7A' : '#212B36',
                }}
              />
              {isExpanded ? (
                <ExpandLess sx={{ color: '#637381' }} />
              ) : (
                <ExpandMore sx={{ color: '#637381' }} />
              )}
            </ListItemButton>
          </ListItem>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child) => renderMenuItem(child, depth + 1))}
            </List>
          </Collapse>
        </Box>
      )
    }

    return (
      <ListItem key={item.title} disablePadding>
        <ListItemButton
          component={NavLink}
          to={item.path}
          onClick={handleItemClick}
          sx={{
            mx: 1,
            ml: depth > 0 ? 4 : 1,
            borderRadius: 2,
            mb: 0.5,
            backgroundColor: isActive(item.path)
              ? 'rgba(27, 94, 122, 0.08)'
              : 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(27, 94, 122, 0.04)',
            },
          }}
        >
          <ListItemIcon sx={{ color: isActive(item.path) ? '#1B5E7A' : '#637381', minWidth: 40 }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.title}
            primaryTypographyProps={{
              fontSize: '0.9rem',
              fontWeight: isActive(item.path) ? 600 : 400,
              color: isActive(item.path) ? '#1B5E7A' : '#212B36',
            }}
          />
        </ListItemButton>
      </ListItem>
    )
  }

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        borderRight: '1px solid rgba(145, 158, 171, 0.2)',
      }}
    >
      {/* Logo/Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          component="img"
          src="/images/uci-logo.png"
          alt="Uganda Cancer Institute"
          sx={{
            height: 36,
            width: 'auto',
            maxWidth: '100%',
            objectFit: 'contain',
          }}
        />
        <Box textAlign="center">
          <Typography
            variant="subtitle1"
            fontWeight={600}
            color="#212B36"
            lineHeight={1.2}
          >
            CMMS
          </Typography>
          <Typography
            variant="caption"
            color="#637381"
          >
            Maintenance System
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        <List>
          {filteredMenuItems.map((item) => renderMenuItem(item))}
        </List>
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid rgba(145, 158, 171, 0.2)' }}>
        <Typography
          component="a"
          href="https://uci.or.ug/"
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          sx={{
            color: '#919EAB',
            textDecoration: 'none',
            display: 'block',
            textAlign: 'center',
            '&:hover': { color: '#1B5E7A' },
          }}
        >
          Uganda Cancer Institute
        </Typography>
        <Typography
          variant="caption"
          color="#919EAB"
          textAlign="center"
          display="block"
          mt={0.5}
        >
          v1.0.0
        </Typography>
      </Box>
    </Box>
  )

  // On mobile, always use temporary drawer
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            border: 'none',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    )
  }

  // On desktop, use persistent drawer that can be hidden
  return (
    <Drawer
      variant="persistent"
      open={open}
      sx={{
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          border: 'none',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  )
}

export default Sidebar
